import { Injectable } from '@angular/core';
import { gsap } from 'gsap';
import { registerRsPlugins, RsMotionService } from '../shared/motion';
import { rsFromTo } from '../shared/motion/rs-motion';

/**
 * Motion choreography for the Contact Us page.
 *
 * All tweens live in one `gsap.context()` scoped to the page root so the whole
 * timeline can be torn down with a single `revert()` from `ngOnDestroy`.
 *
 * Guarantees:
 * • SSR-safe — nothing runs unless `RsMotionService.enabled` is true.
 * • `prefers-reduced-motion` short-circuits every tween.
 * • Only `transform`, `opacity` and `filter` are animated.
 * • Entrances are GSAP `from()` tweens, so no element is hidden by CSS. If JS
 *   never runs the page renders complete.
 * • `will-change` is applied as a tween starts and released when it ends.
 * • The footer is deliberately untouched: it is a separate, already-approved
 *   component and animating another component's DOM from here would reach
 *   outside this page's scope.
 */
@Injectable()
export class ContactMotionService {
  private ctx?: gsap.Context;
  private cleanups: Array<() => void> = [];

  constructor(private motion: RsMotionService) {}

  init(root: HTMLElement): void {
    if (!this.motion.enabled) return;

    this.motion.arm();
    registerRsPlugins();

    this.ctx = gsap.context(() => {
      this.buildHero();
      this.buildArtAmbient();
      this.buildForm();
      this.buildInfoCards();
      this.buildMap();
      this.buildCta();
    }, root);

    // cursor parallax (8–20px range, set per layer via --d) + ambient pause
    const hero = root.querySelector<HTMLElement>('.cu-hero');
    if (hero) {
      this.cleanups.push(this.motion.trackPointer(hero), this.motion.pauseOffscreen(hero));
    }
  }

  /* ------------------------------------------------------------------ hero -- */

  private buildHero(): void {
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    tl.fromTo('.cu-hero__badge', ...rsFromTo({ opacity: 0, scale: 0.86, duration: 0.55 }))
      .fromTo(
        '.cu-hero__title', ...rsFromTo({
          opacity: 0,
          y: 80,
          duration: 0.9,
          onStart: () => this.hold('.cu-hero__title'),
          onComplete: () => this.release('.cu-hero__title'),
        }),
        '-=0.25'
      )
      .fromTo('.cu-hero__rule', ...rsFromTo({ opacity: 0, scaleX: 0, duration: 0.6 }), '-=0.55')
      .fromTo('.cu-hero__lede', ...rsFromTo({ opacity: 0, y: 26, duration: 0.7 }), '-=0.45')
      .fromTo('.cu-perk', ...rsFromTo({ opacity: 0, y: 22, duration: 0.6, stagger: 0.12 }), '-=0.4')
      .fromTo('.cu-hero__social-label', ...rsFromTo({ opacity: 0, y: 16, duration: 0.5 }), '-=0.35')
      .fromTo(
        '.cu-social__item', ...rsFromTo({ opacity: 0, y: 20, duration: 0.5, stagger: 0.07, ease: 'back.out(1.7)' }),
        '-=0.3'
      );

    // illustration rises, unrotates and scales up alongside the copy
    tl.fromTo(
      '.cu-art', ...rsFromTo({
        opacity: 0,
        scale: 0.85,
        rotate: -6,
        duration: 1,
        onStart: () => this.hold('.cu-art'),
        onComplete: () => this.release('.cu-art'),
      }),
      '-=1.35'
    )
      .fromTo('.cu-art__bubble', ...rsFromTo({ opacity: 0, x: -26, duration: 0.55, stagger: 0.1 }), '-=0.5')
      .fromTo(
        '.cu-art__note', ...rsFromTo({ opacity: 0, scale: 0.75, duration: 0.55, stagger: 0.09, ease: 'back.out(1.6)' }),
        '-=0.4'
      );
  }

  /**
   * Endless float on the illustration parts. Driven by GSAP on the `transform`
   * channel — the parallax rides on the separate `translate` property, so the
   * two never overwrite each other (a CSS keyframe animation on `transform`
   * would silently swallow the entrance tweens above).
   */
  private buildArtAmbient(): void {
    const float = (selector: string, y: number, duration: number, delay = 0) => {
      gsap.utils.toArray<HTMLElement>(selector).forEach((el, i) => {
        gsap.to(el, {
          y,
          duration,
          delay: delay + i * 0.22,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
        });
      });
    };

    float('.cu-art__phone', -10, 3.4);
    float('.cu-art__envelope', -14, 3.9, 0.3);
    float('.cu-art__note', -8, 3.1, 0.15);
    float('.cu-art__bubble', -6, 2.8, 0.4);
  }

  /* ------------------------------------------------------------------ form -- */

  private buildForm(): void {
    gsap.fromTo('.cu-panel', ...rsFromTo({
      opacity: 0,
      x: 46,
      duration: 0.9,
      ease: 'power3.out',
      scrollTrigger: { trigger: '.cu-panel', start: 'top 92%', once: true },
    }));

    gsap.fromTo('.cu-panel__head > *, .cu-field, .cu-panel__foot > *', ...rsFromTo({
      opacity: 0,
      y: 20,
      duration: 0.55,
      stagger: 0.06,
      ease: 'power3.out',
      scrollTrigger: { trigger: '.cu-panel', start: 'top 88%', once: true },
    }));
  }

  /* ----------------------------------------------------------- info cards -- */

  private buildInfoCards(): void {
    gsap.fromTo('.contact-block', ...rsFromTo({
      opacity: 0,
      y: 34,
      duration: 0.7,
      stagger: 0.1,
      ease: 'power3.out',
      clearProps: 'transform,opacity',
      scrollTrigger: { trigger: '.cu-cards', start: 'top 88%', once: true },
    }));
  }

  /* ------------------------------------------------------------------- map -- */

  private buildMap(): void {
    const tl = gsap.timeline({
      defaults: { ease: 'power3.out' },
      scrollTrigger: { trigger: '.cu-map', start: 'top 86%', once: true },
    });

    tl.fromTo('.cu-map__shell', ...rsFromTo({ opacity: 0, y: 40, duration: 0.85 }))
      .fromTo('.cu-map__frame', ...rsFromTo({ opacity: 0, scale: 0.95, duration: 0.9 }), '-=0.6')
      .fromTo('.cu-map__badge', ...rsFromTo({ opacity: 0, y: -14, duration: 0.5 }), '-=0.55')
      .fromTo('.cu-map__meta', ...rsFromTo({ opacity: 0, y: 16, duration: 0.5 }), '-=0.4')
      .fromTo(
        '.cu-map__pin', ...rsFromTo({ opacity: 0, y: -34, scale: 0.6, duration: 0.7, ease: 'back.out(2)' }),
        '-=0.35'
      );

    // gentle continuous bounce once the pin has landed
    tl.add(() => {
      gsap.to('.cu-map__pin', {
        y: -8,
        duration: 1.5,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      });
    });
  }

  /* ------------------------------------------------------------------- cta -- */

  private buildCta(): void {
    gsap.fromTo('.cu-cta__panel', ...rsFromTo({
      opacity: 0,
      y: 38,
      scale: 0.97,
      duration: 0.85,
      ease: 'power3.out',
      scrollTrigger: { trigger: '.cu-cta', start: 'top 88%', once: true },
    }));

    gsap.fromTo('.cu-cta__inner > *', ...rsFromTo({
      opacity: 0,
      y: 22,
      duration: 0.6,
      stagger: 0.08,
      ease: 'power3.out',
      scrollTrigger: { trigger: '.cu-cta', start: 'top 84%', once: true },
    }));
  }

  /* --------------------------------------------------------------- helpers -- */

  private hold(selector: string): void {
    gsap.utils
      .toArray<HTMLElement>(selector)
      .forEach((el) => (el.style.willChange = 'transform, opacity'));
  }

  private release(selector: string): void {
    gsap.utils.toArray<HTMLElement>(selector).forEach((el) => (el.style.willChange = 'auto'));
  }

  destroy(): void {
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
    this.ctx?.revert();
    this.ctx = undefined;
  }
}
